import { Component, EventEmitter, OnInit, OnDestroy, Output } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { BalanceService } from 'src/app/api/balance.service';
import { Currency, CurrencyType } from 'src/app/models/currency';
import { Balance } from 'src/app/models/balance';
import { WebsocketBalancesService } from 'src/app/exchange/services/websocket-balances.service';
import { WebSocketService } from 'src/app/api/web-socket.service';
import { environment } from 'src/environments/environment';
import { PnlService } from 'src/app/pnl/services/pnl.service';
import { PnlData } from 'src/app/pnl/models/pnl';
import { SegmentAnalyticsService } from 'src/app/services/segment-analytics.service';
import { ProPnlEvents } from 'src/constants/events/pnl';
import { ExchangeService } from 'src/app/exchange/services/exchange.service';

@Component({
  selector: 'exchange-balance',
  templateUrl: './exchange-balance.component.html',
  styleUrls: ['./exchange-balance.component.scss'],
})
export class ExchangeBalanceComponent implements OnInit, OnDestroy {
  private readonly PRO_URL = '/pro';
  readonly destroy$ = new Subject();
  readonly pnlData$ = new BehaviorSubject<PnlData>(null);
  readonly routerSubscription = this.router.events.pipe(
    takeUntil(this.destroy$),
    filter(e => e instanceof NavigationEnd),
    filter((event: NavigationEnd) => event?.url?.startsWith(this.PRO_URL)),
    switchMap(() => this.pnlService.getDailyPnl(this.defaultFiatCurrency))
  ).subscribe((value) => {
    this.pnlData$.next(value);
  }, err => {
    this.pnlData$.next(null);
  });

  loading = false;
  error: string = null;
  total: { amount: number; currency: string };
  usdTotal: { amount: number; currency: string };
  pair: string;

  data: { currency: Currency; balance: Balance }[] = null;
  fiatData: { currency: Currency; balance: Balance }[] = null;
  cryptoData: { currency: Currency; balance: Balance }[] = null;

  @Output() close = new EventEmitter<boolean>();

  defaultFiatCurrency = environment.currencies.defaultCurrency;
  globalCurrency = environment.currencies.globalCurrency;

  constructor(
    private wss: WebSocketService,
    private exchangeService: ExchangeService,
    private websocketBalancesService: WebsocketBalancesService,
    private balanceService: BalanceService,
    private pnlService: PnlService,
    private router: Router,
    private segmentAnalytics: SegmentAnalyticsService,
  ) {}

  ngOnInit() {
    this.getData();

    this.exchangeService.pairChanges.pipe(takeUntil(this.destroy$)).subscribe((pair) => {
      this.pair = pair;
    });

    this.websocketBalancesService.get().pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.getData();
    });

    this.wss.connectionChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.getData();
    });
  }

  ngOnDestroy() {
    this.destroy$.complete();
  }

  getData() {
    this.loading = true;
    this.error = null;
    this.balanceService.getExchange().pipe(takeUntil(this.destroy$)).subscribe(
      (response) => {
        this.loading = false;
        this.data = response.filter((item) => item.balance);

        this.calculateTotal();

        const [fiat, crypto] = this._partition(this.data, (item) =>
          this.isFiatCurrency(item?.currency)
        );

        this.fiatData = fiat;
        this.cryptoData = crypto;
      },
      (err) => {
        this.loading = false;
        this.error = err.errors[0].message;
      }
    );
  }

  onClose(event: Event) {
    event.preventDefault();
    this.close.emit(false);
  }

  onPnLClick() {
    this.segmentAnalytics.SendTrack(ProPnlEvents.ProPnlCTA, {
      pair: this.pair,
      pairType: this.exchangeService.getPairType(this.pair)
    });
    this.router.navigate(['/pro/pnl']);
  }

  isFiatCurrency(currency: Currency) {
    return currency.type === CurrencyType.Fiat;
  }

  calculateTotal() {
    let total = 0;
    let usdTotal = 0;

    this.data?.forEach((item) => {
      total += parseFloat(item?.balance?.value?.amount || '0');
      usdTotal += parseFloat(item?.balance?.usdValue?.amount || '0');
    });

    this.total = {
      amount: total,
      currency: this?.data?.[0].balance?.value?.currency,
    };

    this.usdTotal = {
      amount: usdTotal,
      currency: this?.data?.[0].balance?.usdValue?.currency,
    };
  }

  private _partition<T>(source: Array<T>, predicate: (T) => boolean) {
    return source.reduce(
      (result, currentCrypto) => {
        const [passed, failed] = result;
        if (predicate(currentCrypto)) {
          passed.push(currentCrypto);
        } else {
          failed.push(currentCrypto);
        }
        return result;
      },
      [[], []]
    );
  }
}
